import {
  DateTimeFormatter,
  Emoji,
  getTranslatedText,
  useIntl,
  withErrorBoundary
} from '@nile/components/src';
import { SVGIcon } from '@superops/super-icons';
import { Badge, TextStyle } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React from 'react';

const SLARenderComponent = (props) => {
  const { mainText, data, defaultSVG } = props;

  const svgIcon = data?.svgIcon;
  const svgColor = data?.svgColor;
  const badgeText = data?.badgeText;
  const badgeVariant = data?.badgeVariant;
  const date = data?.date;
  const timeVariant = data?.timeVariant;

  const intl = useIntl();

  return (
    <div>
      <div className="flex actr fx-sb wrap g5">
        <div className="flex actr g5 mr10">
          <div className="flex">
            {svgIcon === 'Popper' ? (
              <Emoji symbol="🎉" label="popper" />
            ) : (
              <SVGIcon name={svgIcon || defaultSVG} color={svgColor} />
            )}
          </div>
          <div>
            <TextStyle>{mainText}</TextStyle>
          </div>
        </div>

        {badgeText && (
          <Badge status={badgeVariant}>
            <TextStyle weight="Medium">
              <div>{getTranslatedText(intl, badgeText)}</div>
            </TextStyle>
          </Badge>
        )}
      </div>

      <TextStyle variation={timeVariant || 'subdued'}>
        <div className="mt5 fsize12">
          {date ? <DateTimeFormatter type="dateTime" date={date} /> : 'NA'}
        </div>
      </TextStyle>
    </div>
  );
};

const getSlaData = (svgIcon, violation, time, duetime, pausetime) => {
  let slaData;
  const filledIcon = `${svgIcon}Filled`;

  if (pausetime) {
    // SLA paused
    slaData = {
      svgIcon: filledIcon,
      svgColor: '#FADCBF',
      date: pausetime,
      timeVariant: 'warning',
      badgeText: 'ticket.list.resolutionTimer.paused',
      badgeVariant: 'attention'
    };
  }

  if (violation && time && !pausetime) {
    // SLA achieved after due time
    slaData = {
      svgIcon: 'Popper',
      date: time,
      timeVariant: 'negative'
    };
  }

  if (violation && duetime && !pausetime) {
    // SLA overdue
    slaData = {
      svgIcon: filledIcon,
      svgColor: '#F7C1C1',
      date: duetime,
      timeVariant: 'negative',
      badgeText: 'ticket.details.insights.sla.overdue',
      badgeVariant: 'critical'
    };
  }

  if (!violation && duetime && !pausetime) {
    // SLA running
    slaData = {
      svgIcon: svgIcon,
      date: duetime,
      timeVariant: 'subdued'
    };
  }

  if (!violation && time && !pausetime) {
    // SLA achieved
    slaData = {
      svgIcon: 'Popper',
      date: time,
      timeVariant: 'positive',
      badgeText: 'ticket.list.resolutionTimer.achieved',
      badgeVariant: 'success'
    };
  }

  return slaData;
};

const TicketSLA = (props) => {
  const {
    sla,
    resolutionViolated,
    resolutionTime,
    resolutionDueTime,
    firstResponseViolated,
    firstResponseTime,
    firstResponseDueTime,
    resolutionPauseTime,
    hideName
  } = props;

  const intl = useIntl();

  const firstResponseData = getSlaData(
    'Response',
    firstResponseViolated,
    firstResponseTime,
    firstResponseDueTime
  );
  const resolutionData = getSlaData(
    'Flag',
    resolutionViolated,
    resolutionTime,
    resolutionDueTime,
    resolutionPauseTime
  );

  return (
    <div id="ticket-sla-details">
      {!hideName && (
        <TextStyle weight="SemiBold">
          <div>{sla?.name || 'SLA'}</div>
        </TextStyle>
      )}

      <div className="mt10">
        <SLARenderComponent
          mainText={getTranslatedText(intl, 'common.first.response.time')}
          data={firstResponseData}
          defaultSVG="Response"
        />
      </div>

      <div className="mt10">
        <SLARenderComponent
          mainText={getTranslatedText(
            intl,
            'kanbanview.actionlist.ticketResolutionTime'
          )}
          data={resolutionData}
          defaultSVG="Flag"
        />
      </div>
    </div>
  );
};

export default withErrorBoundary(TicketSLA);
